import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class CompanySettingsReadOnly extends ModelObject<CompanySettingsReadOnly> {

    public version: number = 0;
    private _latitude: number | null = null;
    /**
    * Latitude (if known)
    */
    public get latitude(): number | null { return this._latitude }
    public set latitude(value: number | null) {
      this._latitude = value === undefined ? null : value;
    }
    private _longitude: number | null = null;
    /**
    * Longitude (if known)
    */
    public get longitude(): number | null { return this._longitude }
    public set longitude(value: number | null) {
      this._longitude = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.latitude = jsonSerializedObject.latitude == null ? null : jsonSerializedObject.latitude;
          this.longitude = jsonSerializedObject.longitude == null ? null : jsonSerializedObject.longitude;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.latitude = this.latitude == null ? null : this.latitude;
        toRet.longitude = this.longitude == null ? null : this.longitude;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
    ];
}


export class CompanySettings extends ModelObjectWithDirtyCheck<CompanySettings> {

    /**
    * Account Number
    * Not Nullable
    * Max Length : 1000
    */
    public accountNumber: string = "";
    /**
    * Address Line 1
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address Line 2
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address Line 3
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address Line 4
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address Line 5
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address Line 6
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Alternative Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public alternativeName: string = "";
    /**
    * AWRS Number
    * Not Nullable
    * Max Length : 1000
    */
    public awrsNumber: string = "";
    /**
    * Bank Name
    * Not Nullable
    * Max Length : 1000
    */
    public bankName: string = "";
    /**
    * Company Registration Number
    * Not Nullable
    * Max Length : 1000
    */
    public companyRegistrationNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * EORI Number
    * Not Nullable
    * Max Length : 1000
    */
    public eoriNumber: string = "";
    /**
    * EORI Number (NI)
    * Not Nullable
    * Max Length : 1000
    */
    public eoriNumberNi: string = "";
    private _logoImageId: Guid | null = null;
    /**
    * Main Company Logo
    */
    public get logoImageId(): Guid | null { return this._logoImageId }
    public set logoImageId(value: Guid | null) {
      this._logoImageId = value === undefined ? null : value;
    }
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Postcode
    * Not Nullable
    * Max Length : 1000
    */
    public postcode: string = "";
    /**
    * Sort Code
    * Not Nullable
    * Max Length : 1000
    */
    public sortCode: string = "";
    /**
    * Telephone Number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Use Alternative Name on Invoices
    */
    public useAlternativeNameOnInvoices: boolean = false;
    /**
    * VAT Registration No
    * Not Nullable
    * Max Length : 1000
    */
    public vatRegistrationNumber: string = "";
    /**
    * Web
    * Not Nullable
    * Max Length : 1000
    */
    public website: string = "";
    public readOnly: CompanySettingsReadOnly = new CompanySettingsReadOnly();
    /**
    * International Bank Account Number
    * Not Nullable
    * Max Length : 1000
    */
    public iban: string = "";
    /**
    * Bank Identification Code
    * Not Nullable
    * Max Length : 1000
    */
    public bic: string = "";
    private _creditLimitThreshold: number | null = null;
    /**
    * The level at which the user should be notified that the credit balance is near the credit limit.
    * Not Negative
    */
    public get creditLimitThreshold(): number | null { return this._creditLimitThreshold }
    public set creditLimitThreshold(value: number | null) {
      this._creditLimitThreshold = value === undefined ? null : value;
    }
    public australianBusinessNumber: string = "";
    public employerIdentificationNumber: string = "";
    public ttbBrewersNotice: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.accountNumber = jsonSerializedObject.accountNumber;
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.alternativeName = jsonSerializedObject.alternativeName;
          this.awrsNumber = jsonSerializedObject.awrsNumber;
          this.bankName = jsonSerializedObject.bankName;
          this.companyRegistrationNumber = jsonSerializedObject.companyRegistrationNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.eoriNumber = jsonSerializedObject.eoriNumber;
          this.eoriNumberNi = jsonSerializedObject.eoriNumberNi;
          this.logoImageId = jsonSerializedObject.logoImageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.logoImageId);
          this.name = jsonSerializedObject.name;
          this.postcode = jsonSerializedObject.postcode;
          this.sortCode = jsonSerializedObject.sortCode;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.useAlternativeNameOnInvoices = jsonSerializedObject.useAlternativeNameOnInvoices;
          this.vatRegistrationNumber = jsonSerializedObject.vatRegistrationNumber;
          this.website = jsonSerializedObject.website;
          this.readOnly = new CompanySettingsReadOnly(jsonSerializedObject.readOnly);
          this.iban = jsonSerializedObject.iban;
          this.bic = jsonSerializedObject.bic;
          this.creditLimitThreshold = jsonSerializedObject.creditLimitThreshold == null ? null : jsonSerializedObject.creditLimitThreshold;
          this.australianBusinessNumber = jsonSerializedObject.australianBusinessNumber;
          this.employerIdentificationNumber = jsonSerializedObject.employerIdentificationNumber;
          this.ttbBrewersNotice = jsonSerializedObject.ttbBrewersNotice;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.accountNumber = this.accountNumber;
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.alternativeName = this.alternativeName;
        toRet.awrsNumber = this.awrsNumber;
        toRet.bankName = this.bankName;
        toRet.companyRegistrationNumber = this.companyRegistrationNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.eoriNumber = this.eoriNumber;
        toRet.eoriNumberNi = this.eoriNumberNi;
        toRet.logoImageId = this.logoImageId == null ? null : this.logoImageId.toString();
        toRet.name = this.name;
        toRet.postcode = this.postcode;
        toRet.sortCode = this.sortCode;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.useAlternativeNameOnInvoices = this.useAlternativeNameOnInvoices;
        toRet.vatRegistrationNumber = this.vatRegistrationNumber;
        toRet.website = this.website;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.iban = this.iban;
        toRet.bic = this.bic;
        toRet.creditLimitThreshold = this.creditLimitThreshold == null ? null : this.creditLimitThreshold;
        toRet.australianBusinessNumber = this.australianBusinessNumber;
        toRet.employerIdentificationNumber = this.employerIdentificationNumber;
        toRet.ttbBrewersNotice = this.ttbBrewersNotice;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "accountNumber", rule: (v: any) => v == null || v.length <= 1000 || "AccountNumber has too many characters (1000)"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "alternativeName", rule: (v: any) => v == null || v.length !== 0 || "AlternativeName cannot be empty"},
            {field: "alternativeName", rule: (v: any) => v == null || v.length <= 1000 || "AlternativeName has too many characters (1000)"},
            {field: "awrsNumber", rule: (v: any) => v == null || v.length <= 1000 || "AwrsNumber has too many characters (1000)"},
            {field: "bankName", rule: (v: any) => v == null || v.length <= 1000 || "BankName has too many characters (1000)"},
            {field: "companyRegistrationNumber", rule: (v: any) => v == null || v.length <= 1000 || "CompanyRegistrationNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "eoriNumber", rule: (v: any) => v == null || v.length <= 1000 || "EoriNumber has too many characters (1000)"},
            {field: "eoriNumberNi", rule: (v: any) => v == null || v.length <= 1000 || "EoriNumberNi has too many characters (1000)"},
            {field: "logoImageId", rule: (v: any) => v == null || !v.isEmpty() || "CompanySettings.LogoImageId cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
            {field: "sortCode", rule: (v: any) => v == null || v.length <= 1000 || "SortCode has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "vatRegistrationNumber", rule: (v: any) => v == null || v.length <= 1000 || "VatRegistrationNumber has too many characters (1000)"},
            {field: "website", rule: (v: any) => v == null || v.length <= 1000 || "Website has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "iban", rule: (v: any) => v == null || v.length <= 1000 || "Iban has too many characters (1000)"},
            {field: "bic", rule: (v: any) => v == null || v.length <= 1000 || "Bic has too many characters (1000)"},
            {field: "australianBusinessNumber", rule: (v: any) => v == null || v.length <= 1000 || "AustralianBusinessNumber has too many characters (1000)"},
            {field: "employerIdentificationNumber", rule: (v: any) => v == null || v.length <= 1000 || "EmployerIdentificationNumber has too many characters (1000)"},
            {field: "ttbBrewersNotice", rule: (v: any) => v == null || v.length <= 1000 || "TtbBrewersNotice has too many characters (1000)"},
    ];
}


export class CompanySettingsService extends ServiceBase {

  public async getCompanySettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CompanySettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new CompanySettings(response.companySettings);;
    return this.makeJsonRequest("companySettings/v1/getCompanySettings", req, false, extractor, opts);
  };

  public async updateCompanySettings(companySettings: CompanySettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    companySettings:companySettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("companySettings/v1/updateCompanySettings", req, true, extractor, opts);
  };
}

export const companySettingsService = new CompanySettingsService();